/**
 * FloatingMusicPlayer.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { safeReturnPath } from '../utils/safeReturnPath';
import { isEmailVerified } from '../utils/emailVerified';
import { useLanguage } from '../context/LanguageContext';
import { fmt } from '../i18n/format';
import PlayingFiddleMark from './PlayingFiddleMark';
import './FloatingMusicPlayer.css';

/** Player uses only this many tunes (catalog below may list more). */
const FLOATING_PLAYER_MAX_TUNES = 80;

/** Full catalog — only the first {@link FLOATING_PLAYER_MAX_TUNES} are played. */
const FLOATING_PLAYER_TUNE_CATALOG_RAW = [
    { title: 'Áine', file: 'Aine.mp3' },
    { title: 'Allan Bradbury', file: 'AllanBradbury.mp3' },
    { title: 'Annie Mays Kitchen', file: 'AnnieMaysKitchen.mp3' },
    { title: 'Ashmead', file: 'Ashmead.mp3' },
    { title: 'Barna Norton', file: 'BarnaNorton.mp3' },
    { title: 'Bear In Cage', file: 'BearInCage.mp3' },
    { title: 'Between Sets', file: 'BetweenSets.mp3' },
    { title: 'Big Mitts Small Places', file: 'BigMittsSmallPlaces.mp3' },
    { title: 'Bleecker Street', file: 'BleeckerStreet.mp3' },
    { title: 'Bottomless Cup', file: 'BottomlessCup.mp3' },
    { title: "Cameron's Return", file: 'CameronsReturn.mp3' },
    { title: "Carla's Wedding", file: 'CarlasWedding.mp3' },
    { title: 'Caution To Wind', file: 'CautionToWind.mp3' },
    { title: 'Charlie Stones Rattle', file: 'CharlieStonesRattle.mp3' },
    { title: 'Columba', file: 'Columba.mp3' },
    { title: 'Dog On Lace', file: 'DogOnLace.mp3' },
    { title: 'Dun Green', file: 'DunGreen.mp3' },
    { title: "Eanna's Hornpipe", file: 'EannasHornpipe.mp3' },
    { title: 'Eastern Passage', file: 'EasternPassage.mp3' },
    { title: "Farmers Hall", file: 'FarmersHall.mp3' },
    { title: 'First Week July', file: 'FirstWeekJuly.mp3' },
    { title: 'Five Dollar Fiddle', file: 'FiveDollarFiddle.mp3' },
    { title: 'Friends At Bridge', file: 'FriendsAtBridge.mp3' },
    { title: 'Frogmore Lodge', file: 'FrogmoreLodge.mp3' },
    { title: 'Generous Gesture', file: 'GenerousGesture.mp3' },
    { title: "Glendale's Gentle Man", file: 'GlendalesGentleMan.mp3' },
    { title: 'Goose With Attitude', file: 'GooseWithAttitude.mp3' },
    { title: 'Helen Cowan', file: 'HelenCowan.mp3' },
    { title: 'Invisible Immigrant', file: 'InvisibleImmigrant.mp3' },
    { title: "It's An Orchard You Need", file: 'ItsAnOrchardYouNeed.mp3' },
    { title: "Jacqueline's Jig", file: 'JacquelinesJig.mp3' },
    { title: "Jim Scott's", file: 'JimScotts.mp3' },
    { title: "Joanie Madden's", file: 'JoanieMaddens.mp3' },
    { title: "John Donald's Strathspey", file: 'JohnDonaldsStrathspey.mp3' },
    { title: 'Johnny Beaton', file: 'JohnnyBeaton.mp3' },
    { title: 'Juggler', file: 'Juggler.mp3' },
    { title: 'Julie And Geese', file: 'JulieAndGeese.mp3' },
    { title: 'Liam Lewis', file: 'LiamLewis.mp3' },
    { title: 'Lisa Giles', file: 'LisaGiles.mp3' },
    { title: "Lombard's Lilt", file: 'LombardsLilt.mp3' },
    { title: 'MacKay Point MacLeans', file: 'MacKayPointMacLeans.mp3' },
    { title: 'Maguire In Disguise', file: 'MaguireInDisguise.mp3' },
    { title: "Maire's Return", file: 'MairesReturn.mp3' },
    { title: 'Malodorous Dog', file: 'MalodorousDog.mp3' },
    { title: "Maybelle's Explanation", file: 'MaybellesExplanation.mp3' },
    { title: 'Mildred Leadbeater', file: 'MildredLeadbeater.mp3' },
    { title: 'Missing Keys', file: 'MissingKeys.mp3' },
    { title: 'Mother And Son', file: 'MotherAndSon.mp3' },
    { title: 'Neil Martin', file: 'NeilMartin.mp3' },
    { title: 'New Harbour', file: 'NewHarbour.mp3' },
    { title: 'North Wind', file: 'NorthWind.mp3' },
    { title: 'Paulus', file: 'Paulus.mp3' },
    { title: "Paul Cranford's Jig", file: 'PaulCranfordsJig.mp3' },
    { title: 'Puffin Surgeon', file: 'PuffinSurgeon.mp3' },
    { title: 'Quandary', file: 'Quandary.mp3' },
    { title: 'Razorbill Rant', file: 'RazorbillRant.mp3' },
    { title: 'Sanford MacLellan', file: 'SanfordMacLellan.mp3' },
    { title: 'Seal Island Fiddle', file: 'SealIslandFiddle.mp3' },
    { title: 'Simon MacKenzie', file: 'SimonMacKenzie.mp3' },
    { title: 'Slip Reel', file: 'SlipReel.mp3' },
    { title: 'Snookered', file: 'Snookered.mp3' },
    { title: 'South Harbour', file: 'SouthHarbour.mp3' },
    { title: 'Spring Breakup', file: 'SpringBreakup.mp3' },
    { title: "Stella's Courage", file: 'StellasCourage.mp3' },
    { title: 'Teaspoon Gunpowder', file: 'TeaspoonGunpowder.mp3' },
    { title: 'Theresa Morrison', file: 'TheresaMorrison.mp3' },
    { title: 'Tiny Script', file: 'TinyScript.mp3' },
    { title: "Tommy Basker's Reel", file: 'TommyBaskersReel.mp3' },
    { title: "Tommy Kelly's Hideaway", file: 'TommyKellysHideaway.mp3' },
    { title: "Tully's", file: 'Tullys.mp3' },
    { title: 'Val Boudreau', file: 'ValBoudreau.mp3' },
    { title: 'Visiting Dignitaries', file: 'VisitingDignitaries.mp3' },
    { title: 'Waiting For Wind', file: 'WaitingForWind.mp3' },
    { title: 'Walk Through Ruins', file: 'WalkThroughRuins.mp3' },
    { title: 'Water Colours', file: 'WaterColours.mp3' },
    { title: 'Whitewater', file: 'Whitewater.mp3' },
    { title: 'Angus And Alice', file: 'AngusAndAlice.mp3' },
    { title: 'Baddeck Gathering', file: 'BaddeckGathering.mp3' },
    { title: 'Bannockburn', file: 'Bannockburn.mp3' },
    { title: 'Fenella', file: 'fenella.mp3' },
    { title: 'Union Street', file: 'UnionStreet.mp3' },
    { title: 'Gale Force', file: 'GaleForce.mp3' },
];

const playlist = FLOATING_PLAYER_TUNE_CATALOG_RAW.slice(0, FLOATING_PLAYER_MAX_TUNES);

/** Guests hear this one sample; the full list stays behind a free account. */
const GUEST_SAMPLE_FILE = 'UnionStreet.mp3';
const guestSample =
  FLOATING_PLAYER_TUNE_CATALOG_RAW.find((tune) => tune.file === GUEST_SAMPLE_FILE)
  || FLOATING_PLAYER_TUNE_CATALOG_RAW[0];

function getPlayerBox(isSample = false) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  return {
    w: isMobile ? Math.min(window.innerWidth - 24, 280) : 280,
    h: (isMobile ? 340 : 360) + (isSample ? 92 : 0),
  };
}

const FloatingMusicPlayer = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const { t } = useLanguage();
  const ui = t.ui.player;

  /** Verified accounts get the full list; everyone else can open a one-tune sample. */
  const canUseFullPlayer = isEmailVerified(currentUser);
  const activePlaylist = canUseFullPlayer ? playlist : [guestSample];
  const moreTunesCount = playlist.length;
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(() =>
    canUseFullPlayer ? Math.floor(Math.random() * playlist.length) : 0
  );
  const [volume, setVolume] = useState(7); // Volume on 0-10 scale
  const [progress, setProgress] = useState(0);
  const [isShuffle, setIsShuffle] = useState(false);
  const [position, setPosition] = useState(() => {
    const box = getPlayerBox();
    const isMobile = window.innerWidth <= 768;
    return {
      x: isMobile
        ? Math.max(12, window.innerWidth - box.w - 12)
        : Math.max(0, window.innerWidth - box.w - 50),
      y: isMobile
        ? Math.max(72, window.innerHeight - box.h - 16)
        : Math.max(0, (window.innerHeight - box.h) / 2),
    };
  });
  const [togglePosition, setTogglePosition] = useState(() => ({
    x: Math.max(12, window.innerWidth - 76),
    y: Math.max(12, window.innerHeight - 96),
  }));
  const [isDragging, setIsDragging] = useState(false);
  const [isToggleDragging, setIsToggleDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [toggleDragOffset, setToggleDragOffset] = useState({ x: 0, y: 0 });
  const [toggleDragStartPos, setToggleDragStartPos] = useState({ x: 0, y: 0 });
  const [isSpinning, setIsSpinning] = useState(false);

  const audioRef = useRef(null);
  const playerRef = useRef(null);
  const toggleRef = useRef(null);
  const lastAudioPathRef = useRef(null);

  const currentTrack = activePlaylist[currentTrackIndex] || activePlaylist[0];
  const audioPath = `/mp3s/${currentTrack.file}`;
  const returnTo = safeReturnPath(`${location.pathname}${location.search || ''}`, '/');
  const authQuery = `?returnUrl=${encodeURIComponent(returnTo)}`;

  // Sync src / load / play — play() right after setting src often fails until media is ready
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    let cancelled = false;

    if (lastAudioPathRef.current !== audioPath) {
      lastAudioPathRef.current = audioPath;
      audio.src = audioPath;
      audio.load();
    }

    audio.volume = volume / 10;

    if (!isPlaying) {
      audio.pause();
      return undefined;
    }

    const tryPlay = () => {
      if (cancelled) return;
      audio.play().catch((err) => console.log('Playback prevented:', err));
    };

    if (audio.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      tryPlay();
    } else {
      const onCanPlay = () => {
        audio.removeEventListener('canplay', onCanPlay);
        tryPlay();
      };
      audio.addEventListener('canplay', onCanPlay);
      tryPlay();
      return () => {
        cancelled = true;
        audio.removeEventListener('canplay', onCanPlay);
      };
    }

    return () => {
      cancelled = true;
    };
  }, [audioPath, isPlaying, volume]);

  // Update progress
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      setProgress((audio.currentTime / audio.duration) * 100 || 0);
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('ended', onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrackIndex, canUseFullPlayer]);

  // Sync spinning with playing state
  useEffect(() => {
    setIsSpinning(isPlaying);
  }, [isPlaying]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(err => console.log('Playback prevented:', err));
    }
    setIsPlaying(!isPlaying);
  };

  const handleNext = () => {
    if (!canUseFullPlayer) return;
    if (isShuffle) {
      setCurrentTrackIndex(Math.floor(Math.random() * activePlaylist.length));
    } else {
      setCurrentTrackIndex((prev) => (prev + 1) % activePlaylist.length);
    }
  };

  const onEnded = () => {
    if (!canUseFullPlayer) {
      setIsPlaying(false);
      setProgress(0);
      return;
    }
    handleNext();
  };

  const handlePrev = () => {
    if (!audioRef.current) return;
    if (!canUseFullPlayer || audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }
    setCurrentTrackIndex((prev) => (prev - 1 + activePlaylist.length) % activePlaylist.length);
  };

  const handleRandom = () => {
    if (!canUseFullPlayer) return;
    const randomIndex = Math.floor(Math.random() * activePlaylist.length);
    setCurrentTrackIndex(randomIndex);
    setIsPlaying(true);
  };

  const bumpVolume = (delta) => {
    setVolume((prev) => {
      const next = Math.max(0, Math.min(10, Math.round(prev) + delta));
      if (audioRef.current) {
        audioRef.current.volume = next / 10;
      }
      return next;
    });
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
    const el = e.currentTarget;
    const clickPosition = (e.clientX - el.getBoundingClientRect().left) / el.offsetWidth;
    audio.currentTime = Math.max(0, Math.min(1, clickPosition)) * audio.duration;
  };

  const startHeaderDrag = (clientX, clientY) => {
    setIsDragging(true);
    setDragOffset({
      x: clientX - position.x,
      y: clientY - position.y
    });
  };

  const handleHeaderMouseDown = (e) => {
    if (e.target.closest('.close-btn')) return;
    startHeaderDrag(e.clientX, e.clientY);
  };

  const handleHeaderTouchStart = (e) => {
    if (e.target.closest('.close-btn')) return;
    const touch = e.touches[0];
    if (!touch) return;
    startHeaderDrag(touch.clientX, touch.clientY);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const box = getPlayerBox(!canUseFullPlayer);
        setPosition({
          x: Math.max(0, Math.min(window.innerWidth - box.w, e.clientX - dragOffset.x)),
          y: Math.max(0, Math.min(window.innerHeight - box.h, e.clientY - dragOffset.y)),
        });
      }
      if (toggleDragOffset.x !== 0 || toggleDragOffset.y !== 0) {
        // Start dragging if mouse moved
        if (!isToggleDragging) {
          setIsToggleDragging(true);
        }
        setTogglePosition({
          x: e.clientX - toggleDragOffset.x,
          y: e.clientY - toggleDragOffset.y
        });
      }
    };

    const handleTouchMove = (e) => {
      const touch = e.touches[0];
      if (isDragging) {
        const box = getPlayerBox(!canUseFullPlayer);
        setPosition({
          x: Math.max(0, Math.min(window.innerWidth - box.w, touch.clientX - dragOffset.x)),
          y: Math.max(0, Math.min(window.innerHeight - box.h, touch.clientY - dragOffset.y)),
        });
      }
      if (toggleDragOffset.x !== 0 || toggleDragOffset.y !== 0) {
        // Start dragging if touch moved
        if (!isToggleDragging) {
          setIsToggleDragging(true);
        }
        setTogglePosition({
          x: touch.clientX - toggleDragOffset.x,
          y: touch.clientY - toggleDragOffset.y
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setToggleDragOffset({ x: 0, y: 0 });
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      setToggleDragOffset({ x: 0, y: 0 });
    };

    if (isDragging || (toggleDragOffset.x !== 0 || toggleDragOffset.y !== 0)) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, isToggleDragging, dragOffset, toggleDragOffset]);

  // Toggle button drag handlers
  const handleToggleMouseDown = (e) => {
    e.stopPropagation();
    setToggleDragStartPos({ x: e.clientX, y: e.clientY });
    setToggleDragOffset({
      x: e.clientX - togglePosition.x,
      y: e.clientY - togglePosition.y
    });
  };

  const handleToggleTouchStart = (e) => {
    e.stopPropagation();
    const touch = e.touches[0];
    setToggleDragStartPos({ x: touch.clientX, y: touch.clientY });
    setToggleDragOffset({
      x: touch.clientX - togglePosition.x,
      y: touch.clientY - togglePosition.y
    });
  };

  const handleToggleClick = (e) => {
    // Calculate distance moved
    const distanceMoved = Math.sqrt(
      Math.pow(e.clientX - toggleDragStartPos.x, 2) +
      Math.pow(e.clientY - toggleDragStartPos.y, 2)
    );
    
    // Only open if moved less than 5 pixels (was a click, not a drag)
    if (distanceMoved < 5) {
      setIsOpen(true);
    }
    
    setIsToggleDragging(false);
  };

  const handleToggleTouchEnd = (e) => {
    const touch = e.changedTouches[0];
    // Calculate distance moved
    const distanceMoved = Math.sqrt(
      Math.pow(touch.clientX - toggleDragStartPos.x, 2) +
      Math.pow(touch.clientY - toggleDragStartPos.y, 2)
    );
    
    // Only open if moved less than 5 pixels (was a tap, not a drag)
    if (distanceMoved < 5) {
      setIsOpen(true);
    }
    
    setIsToggleDragging(false);
  };

  const minimizeToWidget = (e) => {
    e.stopPropagation();
    setIsOpen(false);
  };

  useEffect(() => {
    if (canUseFullPlayer) return;
    setCurrentTrackIndex(0);
  }, [canUseFullPlayer]);

  return (
    <>
      <audio ref={audioRef} />
      
      {/* Minimized button */}
      {!isOpen && (
        <div
          className={`music-player-widget-stack${isToggleDragging ? ' dragging' : ''}`}
          style={{
            left: `${togglePosition.x}px`,
            top: `${togglePosition.y}px`,
          }}
        >
          <button
            ref={toggleRef}
            type="button"
            className={`music-player-toggle${isToggleDragging ? ' dragging' : ''}`}
            onClick={handleToggleClick}
            onMouseDown={handleToggleMouseDown}
            onTouchStart={handleToggleTouchStart}
            onTouchEnd={handleToggleTouchEnd}
            title={canUseFullPlayer ? ui.openHint : ui.sampleHint}
          >
            <PlayingFiddleMark playing={isPlaying} size="widget" />
          </button>
          <span className="music-floating-label" aria-hidden="true">{t.nav.tunes}</span>
        </div>
      )}

      {/* Full player */}
      {isOpen && (
        <div 
          ref={playerRef}
          className={`floating-music-player${isDragging ? ' dragging' : ''}${canUseFullPlayer ? '' : ' floating-music-player--sample'}`}
          style={{ 
            left: `${position.x}px`, 
            top: `${position.y}px`,
          }}
        >
          <div
            className="player-header"
            title={ui.dragToMove}
            onMouseDown={handleHeaderMouseDown}
            onTouchStart={handleHeaderTouchStart}
          >
            <span className="drag-handle">⋮⋮</span>
            <span className="player-title">{ui.title}</span>
            <button 
              className="close-btn"
              onClick={minimizeToWidget}
              title={ui.minimize}
            >
              −
            </button>
          </div>

          <PlayingFiddleMark playing={isSpinning} size="panel" />

          {/* Tune picker (title + track # shown in options) */}
          <div className="track-info track-info-compact">
            <p className="player-tune-position" aria-hidden="true">
              {canUseFullPlayer
                ? fmt(ui.tunePosition, {
                    current: currentTrackIndex + 1,
                    total: activePlaylist.length,
                  })
                : ui.sampleBadge}
            </p>
            {canUseFullPlayer ? (
              <div
                className="player-track-select-wrap"
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <select
                  id="floating-player-track-select"
                  className="player-track-select"
                  value={currentTrackIndex}
                  aria-label={fmt(ui.selectAria, {
                    title: currentTrack.title,
                    current: currentTrackIndex + 1,
                    total: activePlaylist.length,
                  })}
                  onChange={(e) => {
                    const idx = parseInt(e.target.value, 10);
                    if (Number.isFinite(idx) && idx >= 0 && idx < activePlaylist.length) {
                      setCurrentTrackIndex(idx);
                      setIsPlaying(true);
                    }
                  }}
                >
                  {activePlaylist.map((tune, i) => (
                    <option key={`${tune.file}-${i}`} value={i}>
                      {i + 1}. {tune.title}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <p className="player-sample-title">{currentTrack.title}</p>
            )}
          </div>

          {/* Seek bar (no time labels — saves space; dropdown shows which tune) */}
          <div
            className="progress-container progress-container-seek-only"
            onClick={handleProgressClick}
            title={ui.seekHint}
          >
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {/* Controls */}
          <div className="player-controls">
            {/* Main playback controls */}
            <div className="player-controls-main">
              <button 
                className="control-btn"
                onClick={handlePrev}
                title={ui.previous}
              >
                ⏮
              </button>
              <button 
                className="control-btn play-btn"
                onClick={togglePlay}
                title={isPlaying ? ui.pause : ui.play}
              >
                {isPlaying ? '⏸' : '▶'}
              </button>
              <button 
                className="control-btn"
                onClick={handleNext}
                title={canUseFullPlayer ? ui.next : ui.guestLockedHint}
                disabled={!canUseFullPlayer}
              >
                ⏭
              </button>
            </div>

            {/* Secondary controls - shuffle, random, volume */}
            <div className="player-controls-secondary">
              <button 
                className={`control-btn shuffle-btn ${isShuffle ? 'active' : ''}`}
                onClick={() => setIsShuffle(!isShuffle)}
                title={canUseFullPlayer ? ui.shuffle : ui.guestLockedHint}
                disabled={!canUseFullPlayer}
              >
                🔀
              </button>
              <button 
                className="control-btn random-btn"
                onClick={handleRandom}
                title={canUseFullPlayer ? ui.random : ui.guestLockedHint}
                disabled={!canUseFullPlayer}
              >
                🎲
              </button>
              <div className="player-volume" role="group" aria-label={fmt(ui.volumeLabel, { level: Math.round(volume) })}>
                <span className={`volume-icon${volume <= 0 ? ' is-muted' : ''}`} aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="16" height="16" focusable="false">
                    <path
                      fill="currentColor"
                      d="M3 9v6h4l5 4V5L7 9H3zm13.5 3c0-1.77-1-3.29-2.5-4.03v8.05c1.5-.74 2.5-2.26 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
                    />
                  </svg>
                </span>
                <button
                  type="button"
                  className="volume-step-btn"
                  onClick={() => bumpVolume(-1)}
                  disabled={volume <= 0}
                  title={ui.volumeDown}
                  aria-label={ui.volumeDown}
                >
                  −
                </button>
                <span className="volume-value" aria-hidden="true">{Math.round(volume)}</span>
                <button
                  type="button"
                  className="volume-step-btn"
                  onClick={() => bumpVolume(1)}
                  disabled={volume >= 10}
                  title={ui.volumeUp}
                  aria-label={ui.volumeUp}
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {!canUseFullPlayer && (
            <div className="player-guest-banner">
              <p>
                {currentUser
                  ? ui.verifyEmail
                  : fmt(ui.guestBanner, { count: moreTunesCount })}
              </p>
              {currentUser ? (
                <Link to="/profile" className="player-guest-cta">
                  {ui.guestCtaVerify}
                </Link>
              ) : (
                <div className="player-guest-actions">
                  <Link to={`/register${authQuery}`} className="player-guest-cta">
                    {ui.guestCtaRegister}
                  </Link>
                  <Link to={`/login${authQuery}`} className="player-guest-signin">
                    {ui.guestCtaSignIn}
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default FloatingMusicPlayer;

